import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { fetchFeed, fetchItems, type HNItem } from "@/lib/hn";
import { PostCard } from "./PostCard";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { FeedSkeleton } from "./FeedSkeleton";
const PAGE_SIZE = 30;

/** Validates a URL before rendering it as an href */
function safeHref(url: string | undefined, fallback: string): string {
  if (!url) return fallback;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return url;
  } catch {
    // Invalid URL — use fallback
  }
  return fallback;
}

export interface FeedListProps {
  feedType?: string;
  title?: string;
}

export function FeedList({ feedType = "topstories", title = "Top Stories" }: FeedListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get("p");
  const page = pageParam ? Math.max(1, parseInt(pageParam, 10)) : 1;

  const handlePageChange = (newPage: number) => {
    setSearchParams({ p: newPage.toString() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // 1. Fetch top story IDs
  const {
    data: storyIds,
    isLoading: isLoadingIds,
    error: errorIds,
  } = useQuery<number[]>({
    queryKey: [feedType],
    queryFn: () => fetchFeed(feedType),
  });

  // 2. Fetch details for current page slice
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const pageIds = storyIds ? storyIds.slice(startIndex, endIndex) : [];
  const totalPages = storyIds ? Math.ceil(storyIds.length / PAGE_SIZE) : 0;

  const {
    data: stories,
    isLoading: isLoadingStories,
  } = useQuery<HNItem[]>({
    queryKey: ["stories", pageIds],
    queryFn: () => fetchItems(pageIds),
    enabled: pageIds.length > 0,
    staleTime: 60 * 1000 * 5, // 5 mins
    placeholderData: keepPreviousData,
  });



  if (isLoadingIds) {
    return <FeedSkeleton />;
  }

  if (errorIds) {
    return (
      <div className="p-4 bg-error/20 text-error rounded-none border border-error">
        Failed to load feed.
      </div>
    );
  }

  // Extract featured stories (first 3) for the hero section if on first page
  const featuredStories = page === 1 && stories ? stories.slice(0, 3) : [];
  const feedStories = page === 1 && stories ? stories.slice(3) : stories;
  const mainFeatured = featuredStories[0];

  return (
    <div className="w-full">
      {/* Hero Section (Only on Page 1 and if we have enough stories) */}
      {page === 1 && mainFeatured && (
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-px mb-12 border border-white/20 bg-white/5">
          {/* Main Featured Story */}
          <div className="lg:col-span-8 p-8 md:p-12 relative flex flex-col justify-end min-h-[520px] border-r border-white/20 overflow-hidden group">
            <div className="absolute inset-0 z-0 overflow-hidden">
              <div className="w-full h-full bg-surface-container opacity-20 group-hover:opacity-40 transition-all duration-1000"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent"></div>
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-6">
                <span className="bg-secondary text-on-secondary px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em]">Featured Pulse</span>
                <span className="text-xs font-bold text-secondary uppercase tracking-widest">{mainFeatured.score} points • {mainFeatured.by}</span>
              </div>
              <a href={safeHref(mainFeatured.url, `/hn/post/${mainFeatured.id}`)} className="block">
                <h1 className="text-5xl md:text-7xl font-headline font-extrabold mb-8 max-w-4xl leading-[0.9] tracking-tighter uppercase italic text-white hover:text-secondary transition-colors">
                  {mainFeatured.title}
                </h1>
              </a>
              <div className="flex items-center gap-8 border-t border-white/20 pt-6">
                <a href={`/hn/post/${mainFeatured.id}`} className="flex items-center gap-2 text-white hover:text-secondary transition-colors uppercase font-black text-xs tracking-widest">
                  <span className="material-symbols-outlined text-xl">forum</span>
                  {mainFeatured.descendants || 0} Comments
                </a>
              </div>
            </div>
          </div>

          {/* Sidebar Featured */}
          <div className="lg:col-span-4 flex flex-col">
            {featuredStories.slice(1, 3).map((story, idx) => (
              <a
                key={story.id}
                href={safeHref(story.url, `/hn/post/${story.id}`)}
                className={`flex-1 p-8 ${idx === 0 ? 'border-b border-white/20 bg-surface-container/40 hover:bg-surface-container' : 'bg-surface-container-high hover:bg-secondary hover:text-on-secondary text-white border-t border-white/10'} transition-all cursor-pointer group flex flex-col justify-center`}
              >
                <span className={`text-[10px] font-black ${idx === 0 ? 'text-secondary' : 'opacity-80'} uppercase tracking-[0.3em] mb-4 block`}>Trending</span>
                <h3 className={`text-2xl font-headline font-bold mb-4 leading-tight uppercase ${idx === 1 ? 'italic' : ''} ${idx === 0 ? 'text-white group-hover:text-secondary' : ''} transition-colors`}>
                  {story.title}
                </h3>
                {idx === 0 ? (
                  <div className="flex justify-between items-center text-[10px] font-bold text-secondary uppercase tracking-widest mt-auto">
                    <span>by {story.by}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest mt-auto">
                    <span>Join the discussion</span>
                    <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </div>
                )}
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Main Content Layout */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Feed */}
        <div className="flex-1">
          <div className="flex items-end justify-between mb-8 border-b-2 border-secondary pb-4">
            <h2 className="text-3xl font-headline font-black uppercase tracking-tighter italic text-white">{title}</h2>
            <div className="flex gap-4 font-label text-[10px] font-black uppercase tracking-widest text-on-surface-variant hidden sm:flex">
              <button className="text-secondary">Most Recent</button>
              <button className="hover:text-white transition-colors">Trending</button>
              <button className="hover:text-white transition-colors">Classic</button>
            </div>
          </div>

          <div className="space-y-2">
            {feedStories?.map((story, idx) => (
              <PostCard
                key={story.id}
                post={story}
                rank={page === 1 ? idx + 4 : startIndex + idx + 1}
              />
            ))}
          </div>

          {storyIds && totalPages > 1 && (
            <div className="py-12 flex flex-col items-center border-t border-white/10 mt-8">
              <div className="flex items-center gap-2 overflow-x-auto max-w-full pb-4">
                <button
                  onClick={() => handlePageChange(Math.max(1, page - 1))}
                  disabled={page === 1 || isLoadingStories}
                  className="px-6 py-2 border-2 border-secondary text-secondary text-xs font-black uppercase tracking-widest hover:bg-secondary hover:text-on-secondary transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-secondary"
                >
                  Prev
                </button>

                <div className="flex items-center gap-1 hidden md:flex">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum = page - 2 + i;
                    if (page <= 3) pageNum = i + 1;
                    if (page >= totalPages - 2) pageNum = totalPages - 4 + i;

                    if (pageNum < 1 || pageNum > totalPages) return null;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={isLoadingStories}
                        className={`min-w-[40px] h-[40px] flex items-center justify-center border-2 text-xs font-black transition-colors ${
                          page === pageNum
                            ? "border-secondary bg-secondary text-on-secondary"
                            : "border-transparent text-on-surface-variant hover:border-white/30"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isLoadingStories}
                  className="px-6 py-2 border-2 border-secondary text-secondary text-xs font-black uppercase tracking-widest hover:bg-secondary hover:text-on-secondary transition-all disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-secondary"
                >
                  Next
                </button>
              </div>
              {isLoadingStories && (
                <div className="flex items-center gap-2 mt-4 text-[10px] font-black tracking-widest text-secondary uppercase animate-pulse">
                  <Loader2 className="w-4 h-4 animate-spin text-secondary" />
                  Loading Data
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="w-full lg:w-96 flex flex-col gap-10">
          {/* User Profile Card (Mock) */}
          <div className="bg-surface-container-high p-6 border-b-8 border-secondary">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border-2 border-secondary p-1 bg-background flex items-center justify-center">
                <span className="material-symbols-outlined text-4xl text-secondary">person</span>
              </div>
              <div>
                <h4 className="font-headline font-black text-2xl uppercase italic text-white leading-none">guest_user</h4>
                <p className="text-xs font-black text-secondary uppercase tracking-[0.2em] mt-1">READ ONLY</p>
              </div>
            </div>
          </div>

          {/* Trending Topics */}
          <div>
            <h3 className="font-headline font-black text-xl uppercase italic mb-6 flex items-center gap-2 text-white">
              <span className="w-1 h-6 bg-secondary"></span>
              Trending Topics
            </h3>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1.5 bg-secondary text-on-secondary text-[10px] font-black uppercase tracking-widest hover:bg-white transition-all cursor-pointer">AI</span>
              <span className="px-3 py-1.5 border border-white/20 text-white text-[10px] font-black uppercase tracking-widest hover:bg-secondary hover:text-on-secondary transition-all cursor-pointer">Rust Lang</span>
              <span className="px-3 py-1.5 border border-white/20 text-white text-[10px] font-black uppercase tracking-widest hover:bg-secondary hover:text-on-secondary transition-all cursor-pointer">WebGPU</span>
              <span className="px-3 py-1.5 border border-white/20 text-white text-[10px] font-black uppercase tracking-widest hover:bg-secondary hover:text-on-secondary transition-all cursor-pointer">PostgreSQL</span>
              <span className="px-3 py-1.5 border border-white/20 text-white text-[10px] font-black uppercase tracking-widest hover:bg-secondary hover:text-on-secondary transition-all cursor-pointer">Startups</span>
            </div>
          </div>

          {/* Hall of Fame */}
          <div className="border border-white/10 p-6 bg-surface-container-low">
            <h3 className="font-headline font-black text-xl uppercase italic mb-6 text-white">Hall of Fame</h3>
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-white/10 pb-2">
                <div className="flex items-center gap-4">
                  <span className="font-headline font-black text-secondary text-xl">01</span>
                  <span className="text-xs font-black uppercase tracking-widest text-white">dang</span>
                </div>
              </div>
              <div className="flex items-center justify-between border-b border-white/10 pb-2">
                <div className="flex items-center gap-4">
                  <span className="font-headline font-black text-secondary text-xl">02</span>
                  <span className="text-xs font-black uppercase tracking-widest text-white">tptacek</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
